//#region header */
/**************************************************************************************************
//
//  Description: MT Documents page
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   21.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   25.02.25 Sean Flook          GMSCM-1 Added code to display the data.
//    003   13.03.25 Sean Flook          GMSCM-1 Added code to handle when a users token has expired.
//    004   05.02.26 Sean Flook          GMSCM-12 Changes required for V2 of the API.
//    005   26.02.26 Joshua McCormick    GMSCM-3 Order of newly added items in configuration and related trees to top.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";
import useApplicationNames from "../hooks/useApplicationNames";

import useConfigs from "../hooks/useConfigs";

import { ArraysEqual, loginStorageName, tokenStorageName, userStorageName } from "../utils/HelperUtils";

import MultiTenantConfigGetDto, {
  DatabaseConnection,
  IndexConnection,
  MultiTenantConfigOrganisation,
  NamedConnectionSetting,
  NamedElasticSetting,
  NewConfigDocumentDto,
} from "../dtos/multiTenantConfigDto";
import { findConfigDocument, getConfigDocumentKey } from "../utils/ConfigNormalization";

import { Backdrop, Box, Breadcrumbs, Button, CircularProgress, Grid2, Link, Stack, Typography } from "@mui/material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

import DocumentForm from "../forms/DocumentForm";
import DocumentTree from "../forms/DocumentTree";
import ElasticNodeForm from "../forms/ElasticNodeForm";
import DocumentOrganisationForm from "../forms/DocumentOrganisationForm";

import AppsIcon from "@mui/icons-material/Apps";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ErrorIcon from "@mui/icons-material/Error";
import HubIcon from "@mui/icons-material/Hub";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SaveIcon from "@mui/icons-material/Save";
import StorageIcon from "@mui/icons-material/Storage";
// import ListIcon from "@mui/icons-material/List";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";

import ConfirmEditLossDialog from "../dialogs/ConfirmEditLossDialog";
import useAddConfig from "../hooks/useAddConfig";
import useUpdateConfig from "../hooks/useUpdateConfig";
import ConfigErrorType from "../models/configErrorType";
import { dataFormStyle, getSaveButtonStyle, toolbarStyle } from "../utils/ADSStyles";
import { adsRed, adsRed10 } from "../utils/ADSColour";
import DbConnectionForm from "../forms/DbConnectionForm";
import IndexConnectionForm from "../forms/IndexConnectionForm";
import ActionDialog, { ActionVariant, DialogMode } from "../dialogs/ActionDialog";
import ConfirmDeleteDialog, { DeleteVariant } from "../dialogs/ConfirmDeleteDialog";
import { validateConfig } from "../utils/ConfigurationValidation";
import useOrganisations from "../hooks/useOrganisations";

interface DocumentsPageProps {}

const DocumentsPage: React.FC<DocumentsPageProps> = () => {
  const displayName = useUserQueryStore((s) => s.userQuery.currentUser?.displayName);
  const logoff = useUserQueryStore((s) => s.logoff);
  const { data: applicationNamesData } = useApplicationNames();
  const { data: organisationsData } = useOrganisations();

  const documentChanged = useConfigQueryStore((s) => s.configQuery.documentChanged);
  const newConfig = useConfigQueryStore((s) => s.configQuery.newConfig);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);
  const organisationSelected = useConfigQueryStore((s) => s.configQuery.organisationSelected);
  const elasticSettingSelected = useConfigQueryStore((s) => s.configQuery.elasticSettingSelected);
  const elasticNodeSelected = useConfigQueryStore((s) => s.configQuery.elasticNodeSelected);
  const disableSave = useConfigQueryStore((s) => s.configQuery.disableSave);
  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const validationErrors = useConfigQueryStore((s) => s.configQuery.validationErrors);
  const setNewConfig = useConfigQueryStore((s) => s.setNewConfig);
  const setDocumentChanged = useConfigQueryStore((s) => s.setDocumentChanged);
  const setConfigSelected = useConfigQueryStore((s) => s.setConfigSelected);
  const setOrganisationSelected = useConfigQueryStore((s) => s.setOrganisationSelected);
  const setElasticSettingSelected = useConfigQueryStore((s) => s.setElasticSettingSelected);
  const setElasticNodeSelected = useConfigQueryStore((s) => s.setElasticNodeSelected);
  const setOriginalDocument = useConfigQueryStore((s) => s.setOriginalDocument);
  const setConfigItem = useConfigQueryStore((s) => s.setConfigItem);
  const setConfigurationDocuments = useConfigQueryStore((s) => s.setConfigurationDocuments);
  const setClearFilter = useConfigQueryStore((s) => s.setClearFilter);
  const setDisableSave = useConfigQueryStore((s) => s.setDisableSave);
  const setRevertChanges = useConfigQueryStore((s) => s.setRevertChanges);

  const { data, error, isLoading } = useConfigs();

  const addConfig = useAddConfig((newData: MultiTenantConfigGetDto[]) => {
    setNewConfig(false);
    modifiedItems.current = [];
    updateConfigurationDocuments(newData);
    originalConfigDocData.current = newData;
    setEnableSave(false);
    setDocumentChanged(false);
    setConfigError(undefined);
    setHaveErrors(false);
    setErrorIds([]);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const updateConfig = useUpdateConfig((updatedData: MultiTenantConfigGetDto[]) => {
    setNewConfig(false);
    modifiedItems.current = [];
    updateConfigurationDocuments(updatedData);
    originalConfigDocData.current = updatedData;
    setEnableSave(false);
    setDocumentChanged(false);
    setConfigError(undefined);
    setHaveErrors(false);
    setErrorIds([]);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [configError, setConfigError] = useState<ConfigErrorType[] | undefined>(undefined);
  const [configDocData, setConfigDocData] = useState<MultiTenantConfigGetDto[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<ReactElement[]>([]);
  const [documentFormData, setDocumentFormData] = useState<MultiTenantConfigGetDto | undefined>();
  const [organisationFormData, setOrganisationFormData] = useState<MultiTenantConfigOrganisation | undefined>();
  const [connectionFormData, setConnectionFormData] = useState<DatabaseConnection | undefined>();
  const [elasticIndexFormData, setElasticIndexFormData] = useState<IndexConnection | undefined>();
  const [elasticNodeFormData, setElasticNodeFormData] = useState<string | undefined>();

  const originalConfigDocData = useRef<MultiTenantConfigGetDto[]>([]);
  const originalDocumentFormData = useRef<MultiTenantConfigGetDto | undefined>(undefined);
  const originalOrganisationFormData = useRef<MultiTenantConfigOrganisation | undefined>(undefined);
  const originalConnectionFormData = useRef<DatabaseConnection | undefined>(undefined);
  const originalElasticIndexFormData = useRef<IndexConnection | undefined>(undefined);
  const originalElasticNodeFormData = useRef<string | undefined>(undefined);

  const modifiedItems = useRef<string[]>([]);
  const newSelectedItemId = useRef<string | undefined>(undefined);
  const saveItemId = useRef<string | undefined>(undefined);
  const bypassUnsavedPromptRef = useRef<boolean>(false);

  const [enableSave, setEnableSave] = useState<boolean>(false);
  const [openConformation, setOpenConformation] = useState<boolean>(false);
  const [haveErrors, setHaveErrors] = useState<boolean>(false);
  const [errorIds, setErrorIds] = useState<string[]>([]);

  const [tokenExpired, setTokenExpired] = useState<boolean>(false);

  const [openActionDialog, setOpenActionDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const deleteType = useRef<DeleteVariant | undefined>(undefined);
  const deleteItemId = useRef<string | undefined>(undefined);
  const deleteRecordName = useRef<string | undefined>(undefined);
  const actionData = useRef<NewConfigDocumentDto | undefined>(undefined);
  const actionMode = useRef<DialogMode>(DialogMode.add);
  const actionType = useRef<ActionVariant>(undefined);

  useEffect(() => {
    if (error) setErrorText(error.message);
  }, [error]);

  /**
   * Method to get the configuration data.
   *
   * @param {MultiTenantConfigGetDto} doc The configuration document.
   */
  const displayDocumentForm = useCallback(
    (doc: MultiTenantConfigGetDto): void => {
      setBreadcrumbs([
        <Typography key="1" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
          <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {doc?.appName}
        </Typography>,
      ]);
      setDocumentFormData(doc);
      originalDocumentFormData.current = doc;
      setOrganisationFormData(undefined);
      originalOrganisationFormData.current = undefined;
      setConnectionFormData(undefined);
      originalConnectionFormData.current = undefined;
      setElasticIndexFormData(undefined);
      originalElasticIndexFormData.current = undefined;
      setElasticNodeFormData(undefined);
      originalElasticNodeFormData.current = undefined;
      setConfigSelected(getConfigDocumentKey(doc));
    },
    [setConfigSelected]
  );

  /**
   * Event to handle when a node from the tree view is selected.
   *
   * @param nodeId The id of the selected node.
   */
  const handleItemSelectedEvent = useCallback(
    (nodeId: string | undefined): void => {
      const nodeIds: string[] = nodeId ? nodeId.split("|") : [];
      const changedDocument: boolean = !selectedItemId || selectedItemId.split("|")[0] !== nodeIds[0];
      newSelectedItemId.current = nodeId;

      if (
        !bypassUnsavedPromptRef.current &&
        changedDocument &&
        documentChanged &&
        selectedItemId &&
        modifiedItems.current.includes(selectedItemId.split("|")[0])
      ) {
        // Show a warning message that there are unsaved changes  and ask the user if they want to continue
        // If they say no, then return and do not change the selected item
        // If they say yes, then continue with the change
        setOpenConformation(true);
      } else {
        if (nodeId) {
          if (nodeIds && nodeIds.length > 0) {
            let selectedDoc: MultiTenantConfigGetDto | undefined = findConfigDocument(configDocData, nodeIds[0]);
            let selectedOrg: MultiTenantConfigOrganisation | undefined = undefined;
            let selectedConnectionSetting: NamedConnectionSetting | undefined = undefined;
            let selectedElasticSetting: NamedElasticSetting | undefined = undefined;
            let selectedNode: string | undefined = undefined;

            if (changedDocument) {
              const clonedDoc = structuredClone(selectedDoc);
              if (clonedDoc) setOriginalDocument(clonedDoc);
            }

            setSelectedItemId(nodeId);
            setConfigItem(nodeId);

            if (selectedDoc && nodeIds.length > 1) {
              selectedOrg = (selectedDoc.organisations ?? []).find((x) => x.organisationName === nodeIds[1]);
              if (selectedOrg && nodeIds.length > 2) {
                selectedConnectionSetting = (selectedOrg.namedConnectionSettings ?? []).find(
                  (x) => x.name === nodeIds[2]
                );
                selectedElasticSetting = (selectedOrg.namedElasticSettings ?? []).find((x) => x.name === nodeIds[2]);
                if (selectedElasticSetting && nodeIds.length > 3) {
                  selectedNode = (selectedElasticSetting.settings?.elasticNodes ?? []).find((x) => x === nodeIds[3]);
                }
              }
            }

            if (!!selectedNode) {
              // Show elastic node information
              if (selectedElasticSetting) {
                setBreadcrumbs([
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    key="1"
                    component="button"
                    color="inherit"
                    onClick={() => handleItemSelectedEvent(getConfigDocumentKey(selectedDoc!))}
                  >
                    <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {selectedDoc?.appName}
                  </Link>,
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    key="2"
                    component="button"
                    color="inherit"
                    onClick={() => handleItemSelectedEvent(`${getConfigDocumentKey(selectedDoc!)}|${selectedOrg!.organisationName}`)}
                  >
                    <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {selectedOrg?.organisationName}
                  </Link>,
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    key="3"
                    component="button"
                    color="inherit"
                    onClick={() =>
                      handleItemSelectedEvent(`${getConfigDocumentKey(selectedDoc!)}|${selectedOrg!.organisationName}|SearchIndex`)
                    }
                  >
                    <ManageSearchIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {selectedElasticSetting?.name}
                  </Link>,
                  <Typography key="4" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
                    <HubIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {selectedNode}
                  </Typography>,
                ]);
              }
              setDocumentFormData(undefined);
              originalDocumentFormData.current = undefined;
              setOrganisationFormData(undefined);
              originalOrganisationFormData.current = undefined;
              setConnectionFormData(undefined);
              originalConnectionFormData.current = undefined;
              setElasticIndexFormData(undefined);
              originalElasticIndexFormData.current = undefined;
              setElasticNodeFormData(selectedNode);
              originalElasticNodeFormData.current = selectedNode;
              setConfigSelected(getConfigDocumentKey(selectedDoc!));
              setOrganisationSelected(selectedOrg!.organisationName);
              setElasticSettingSelected(selectedElasticSetting!.name);
              setElasticNodeSelected(selectedNode);
            } else if (selectedConnectionSetting) {
              // Show search database information
              setBreadcrumbs([
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  key="1"
                  component="button"
                  color="inherit"
                  onClick={() => handleItemSelectedEvent(getConfigDocumentKey(selectedDoc!))}
                >
                  <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedDoc?.appName}
                </Link>,
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  key="2"
                  component="button"
                  color="inherit"
                  onClick={() => handleItemSelectedEvent(`${getConfigDocumentKey(selectedDoc!)}|${selectedOrg!.organisationName}`)}
                >
                  <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedOrg?.organisationName}
                </Link>,
                <Typography key="4" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
                  <StorageIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedConnectionSetting?.name}
                </Typography>,
              ]);
              setDocumentFormData(undefined);
              originalDocumentFormData.current = undefined;
              setOrganisationFormData(undefined);
              originalOrganisationFormData.current = undefined;
              setConnectionFormData(selectedConnectionSetting.settings);
              originalConnectionFormData.current = selectedConnectionSetting.settings;
              setElasticIndexFormData(undefined);
              originalElasticIndexFormData.current = undefined;
              setElasticNodeFormData(undefined);
              originalElasticNodeFormData.current = undefined;
              setConfigSelected(getConfigDocumentKey(selectedDoc!));
              setOrganisationSelected(selectedOrg!.organisationName);
              setElasticSettingSelected(undefined);
              setElasticNodeSelected(undefined);
            } else if (selectedElasticSetting) {
              // Show search index information
              setBreadcrumbs([
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  key="1"
                  component="button"
                  color="inherit"
                  onClick={() => handleItemSelectedEvent(getConfigDocumentKey(selectedDoc!))}
                >
                  <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedDoc?.appName}
                </Link>,
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  key="2"
                  component="button"
                  color="inherit"
                  onClick={() => handleItemSelectedEvent(`${getConfigDocumentKey(selectedDoc!)}|${selectedOrg!.organisationName}`)}
                >
                  <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedOrg?.organisationName}
                </Link>,
                <Typography key="4" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
                  <ManageSearchIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedElasticSetting?.name}
                </Typography>,
              ]);
              setDocumentFormData(undefined);
              originalDocumentFormData.current = undefined;
              setOrganisationFormData(undefined);
              originalOrganisationFormData.current = undefined;
              setConnectionFormData(undefined);
              originalConnectionFormData.current = undefined;
              setElasticIndexFormData(selectedElasticSetting.settings);
              originalElasticIndexFormData.current = selectedElasticSetting.settings;
              setElasticNodeFormData(undefined);
              originalElasticNodeFormData.current = undefined;
              setConfigSelected(getConfigDocumentKey(selectedDoc!));
              setOrganisationSelected(selectedOrg!.organisationName);
              setElasticSettingSelected(selectedElasticSetting!.name);
              setElasticNodeSelected(undefined);
            } else if (selectedOrg) {
              // Show organisation information + elastic settings (elastic is hidden from the tree)
              const primaryElastic = selectedOrg.namedElasticSettings?.[0];
              setBreadcrumbs([
                <Link
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center" }}
                  key="1"
                  component="button"
                  color="inherit"
                  onClick={() => handleItemSelectedEvent(getConfigDocumentKey(selectedDoc!))}
                >
                  <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedDoc?.appName}
                </Link>,
                <Typography key="2" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
                  <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  {selectedOrg?.organisationName}
                </Typography>,
              ]);
              setDocumentFormData(undefined);
              originalDocumentFormData.current = undefined;
              setOrganisationFormData(selectedOrg);
              originalOrganisationFormData.current = selectedOrg;
              setConnectionFormData(undefined);
              originalConnectionFormData.current = undefined;
              setElasticIndexFormData(primaryElastic?.settings);
              originalElasticIndexFormData.current = primaryElastic?.settings;
              setElasticNodeFormData(undefined);
              originalElasticNodeFormData.current = undefined;
              setConfigSelected(getConfigDocumentKey(selectedDoc!));
              setOrganisationSelected(selectedOrg?.organisationName);
              setElasticSettingSelected(primaryElastic?.name);
              setElasticNodeSelected(undefined);
            } else if (selectedDoc) {
              // Show document information
              setConfigSelected(getConfigDocumentKey(selectedDoc!));
              setOrganisationSelected(undefined);
              setConnectionFormData(undefined);
              setElasticIndexFormData(undefined);
              setElasticNodeSelected(undefined);
              displayDocumentForm(selectedDoc);
            } else {
              setBreadcrumbs([]);
              setDocumentFormData(undefined);
              originalDocumentFormData.current = undefined;
              setOrganisationFormData(undefined);
              originalOrganisationFormData.current = undefined;
              setConnectionFormData(undefined);
              originalConnectionFormData.current = undefined;
              setElasticIndexFormData(undefined);
              originalElasticIndexFormData.current = undefined;
              setElasticNodeFormData(undefined);
              originalElasticNodeFormData.current = undefined;
              setConfigSelected(undefined);
              setOrganisationSelected(undefined);
              setElasticSettingSelected(undefined);
              setElasticNodeSelected(undefined);
            }
          }
        } else {
          setSelectedItemId(undefined);
          newSelectedItemId.current = undefined;
          setConfigItem(undefined);
          setBreadcrumbs([]);
          setDocumentFormData(undefined);
          originalDocumentFormData.current = undefined;
          setOrganisationFormData(undefined);
          originalOrganisationFormData.current = undefined;
          setConnectionFormData(undefined);
          originalConnectionFormData.current = undefined;
          setElasticIndexFormData(undefined);
          originalElasticIndexFormData.current = undefined;
          setElasticNodeFormData(undefined);
          originalElasticNodeFormData.current = undefined;
          setConfigSelected(undefined);
          setOrganisationSelected(undefined);
          setElasticSettingSelected(undefined);
          setElasticNodeSelected(undefined);
        }
      }
    },
    [
      configDocData,
      selectedItemId,
      displayDocumentForm,
      documentChanged,
      setConfigItem,
      setOriginalDocument,
      setConfigSelected,
      setOrganisationSelected,
      setElasticSettingSelected,
      setElasticNodeSelected,
    ]
  );

  /**
   * Method to handle the event when the configuration documents are updated.
   *
   * @param documents The updated configuration documents.
   */
  const updateConfigurationDocuments = useCallback(
    (documents: MultiTenantConfigGetDto[]): void => {
      setConfigDocData(documents);
      setConfigurationDocuments(documents);
    },
    [setConfigurationDocuments]
  );

  /**
   * Method to add new items to the configuration document.
   *
   * @param {string} type The type of item to add.
   * @param {string} [id] The id of the currently selected node.
   */
  const handleAddConnectionSetting = (id: string): void => {
    const [documentKey, orgName] = id.split("|");
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, documentKey);
    if (!currentConfiguration) return;

    const currentOrganisation = currentConfiguration.organisations.find((x) => x.organisationName === orgName);
    if (!currentOrganisation) return;

    const existing = currentOrganisation.namedConnectionSettings.filter((x) =>
      x.name.includes("_New Connection Setting")
    );
    const newSetting = {
      name: `_New Connection Setting ${existing.length + 1}`,
      settings: {
        server: undefined as string | undefined,
        database: undefined as string | undefined,
        username: undefined as string | undefined,
        password: undefined as string | undefined,
        port: undefined as number | undefined,
      },
    };

    currentOrganisation.namedConnectionSettings = [newSetting, ...currentOrganisation.namedConnectionSettings];
    updateConfigurationDocuments(
      currentConfigurations.map((x) =>
        x.id === documentKey || x.name === documentKey || x.appName === documentKey
          ? {
              ...x,
              organisations: x.organisations.map((org) =>
                org.organisationName === orgName ? currentOrganisation : org
              ),
            }
          : x
      )
    );

    const settingId = `${documentKey}|${orgName}|${newSetting.name}`;
    setExpanded((prev) => Array.from(new Set([...prev, documentKey, `${documentKey}|${orgName}`, settingId])));
    handleItemSelectedEvent(settingId);
    setDocumentChanged(true);
    setEnableSave(true);
  };

  const handleAddElasticSetting = (id: string): void => {
    const [documentKey, orgName] = id.split("|");
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, documentKey);
    if (!currentConfiguration) return;

    const currentOrganisation = currentConfiguration.organisations.find((x) => x.organisationName === orgName);
    if (!currentOrganisation) return;

    const existing = currentOrganisation.namedElasticSettings.filter((x) => x.name.includes("_New Elastic Setting"));
    const newSetting = {
      name: `_New Elastic Setting ${existing.length + 1}`,
      settings: {
        alias: undefined as string | undefined,
        username: undefined as string | undefined,
        password: undefined as string | undefined,
        fingerprint: undefined as string | undefined,
        elasticNodes: [] as string[],
      },
    };

    currentOrganisation.namedElasticSettings = [newSetting, ...currentOrganisation.namedElasticSettings];
    updateConfigurationDocuments(
      currentConfigurations.map((x) =>
        x.id === documentKey || x.name === documentKey || x.appName === documentKey
          ? {
              ...x,
              organisations: x.organisations.map((org) =>
                org.organisationName === orgName ? currentOrganisation : org
              ),
            }
          : x
      )
    );

    const settingId = `${documentKey}|${orgName}|${newSetting.name}`;
    setExpanded((prev) => Array.from(new Set([...prev, documentKey, `${documentKey}|${orgName}`, settingId])));
    handleItemSelectedEvent(settingId);
    setDocumentChanged(true);
    setEnableSave(true);
  };

  const handleAddItem = (type: string, id?: string | undefined): void => {
    switch (type) {
      case "configuration":
        handleAddConfiguration();
        break;

      case "organisation":
        handleAddOrganisation(id!);
        break;

      case "connectionSetting":
        handleAddConnectionSetting(id!);
        break;

      case "elasticSetting":
        handleAddElasticSetting(id!);
        break;

      case "elasticNode":
        handleAddElasticNode(id!);
        break;

      default:
        break;
    }
  };

  /**
   * Method to maintain the list of modified items.
   *
   * @param {string} docId The id of the document.
   * @param {boolean} hasChanged A flag indicating if the document has changed.
   */
  const updateModifiedItems = (docId: string, hasChanged: boolean): void => {
    const newList: string[] = [...modifiedItems.current];
    const index: number = newList.indexOf(docId);
    if (index === -1 && hasChanged) {
      newList.push(docId);
    } else if (!hasChanged && index !== -1) {
      newList.splice(index, 1);
    }
    modifiedItems.current = newList;
    setDocumentChanged(modifiedItems.current.length > 0);
    setEnableSave(newList.length > 0);
  };

  /**
   * Method to handle the event when a new configuration is added.
   */
  const handleAddConfiguration = (): void => {
    actionType.current = ActionVariant.configuration;
    actionMode.current = DialogMode.add;
    actionData.current = { appName: "", organisation: "" };
    setOpenActionDialog(true);
  };

  /**
   * Method to handle the event when a new organisation is added.
   *
   * @param {string} id The id of the currently selected node.
   */
  const handleAddOrganisation = (id: string): void => {
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, id);
    if (currentConfiguration) {
      const newOrganisations: MultiTenantConfigOrganisation[] = currentConfiguration.organisations.filter((x) =>
        x.organisationName.includes("_New Organisation")
      );

      const newOrganisation: MultiTenantConfigOrganisation = {
        organisationName: `_New Organisation ${newOrganisations.length + 1}`,
        namedConnectionSettings: [
          {
            name: "_New Connection Setting",
            settings: {
              server: undefined,
              database: undefined,
              username: undefined,
              password: undefined,
              port: undefined,
            },
          },
        ],
        namedElasticSettings: [
          {
            name: "_New Elastic Setting",
            settings: {
              alias: undefined,
              username: undefined,
              password: undefined,
              elasticNodes: [],
            },
          },
        ],
      };

      currentConfiguration.organisations = [newOrganisation, ...currentConfiguration.organisations];

      const updatedConfigurations = currentConfigurations.map((x) =>
        x.id === id || x.name === id || x.appName === id ? currentConfiguration : x
      );
      updateConfigurationDocuments(updatedConfigurations);

      const orgNodeId = `${id}|${newOrganisation.organisationName}`;
      const firstConnectionId = `${orgNodeId}|${newOrganisation.namedConnectionSettings[0].name}`;
      const newExpanded: string[] = Array.from(new Set([...expanded, id, orgNodeId]));
      setExpanded(newExpanded);
      // Land on the connection form — those fields aren't on the organisation screen.
      handleItemSelectedEvent(firstConnectionId);
      setDocumentChanged(true);
      setEnableSave(true);
    }
  };

  /**
   * Method to handle the event when a new elastic node is added.
   *
   * @param {string} id The id of the currently selected node.
   */
  const handleAddElasticNode = (id: string): void => {
    const nodeIds: string[] = id.split("|");
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, nodeIds[0]);
    if (currentConfiguration) {
      const currentOrganisation = currentConfiguration.organisations.find((x) => x.organisationName === nodeIds[1]);
      if (currentOrganisation) {
        if (nodeIds.length < 2) return;
        const currentElasticSetting = currentOrganisation.namedElasticSettings.find((x) => x.name === nodeIds[2]);
        if (!currentElasticSetting) return;
        const newElasticNodes: string[] = currentElasticSetting.settings.elasticNodes.filter((x) =>
          x.includes("_New Elastic Node")
        );
        const newElasticNode: string = `_New Elastic Node ${newElasticNodes.length + 1}`;
        currentElasticSetting.settings.elasticNodes = [
          newElasticNode,
          ...currentElasticSetting.settings.elasticNodes,
        ];
        const updatedConfigurations = currentConfigurations.map((x) =>
          (x.id === nodeIds[0] || x.name === nodeIds[0] || x.appName === nodeIds[0])
            ? {
                ...x,
                organisations: x.organisations.map((y) =>
                  y.organisationName === nodeIds[1] ? currentOrganisation : y
                ),
              }
            : x
        );
        updateConfigurationDocuments(updatedConfigurations);

        const newExpanded: string[] = [...expanded];
        if (!newExpanded.includes(id)) {
          newExpanded.push(id);
          setExpanded(newExpanded);
        }

        handleItemSelectedEvent(`${id}|${newElasticNode}`);
        setDocumentChanged(true);
        setEnableSave(true);
      }
    }
  };

  /**
   * Method to handle the event when an item is deleted.
   *
   * @param {string} type The type of item to delete.
   * @param {string} id The id of the item to delete.
   */
  const handleDeleteItem = (type: string, id: string): void => {
    switch (type) {
      case "organisation":
        deleteType.current = DeleteVariant.organisation;
        deleteItemId.current = id;
        deleteRecordName.current = id.split("|")[1];
        setOpenDeleteDialog(true);
        break;

      case "elasticNode":
        deleteType.current = DeleteVariant.elasticNode;
        deleteItemId.current = id;
        deleteRecordName.current = id.split("|")[3];
        setOpenDeleteDialog(true);
        break;

      default:
        break;
    }
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed: boolean): void => {
    if (deleteConfirmed && deleteItemId.current && deleteType.current !== undefined) {
      if (deleteType.current === DeleteVariant.organisation) {
        handleDeleteOrganisation(deleteItemId.current, true);
      } else if (deleteType.current === DeleteVariant.elasticNode) {
        handleDeleteElasticNode(deleteItemId.current, true);
      }
    }

    deleteType.current = undefined;
    deleteItemId.current = undefined;
    deleteRecordName.current = undefined;
    setOpenDeleteDialog(false);
  };

  /**
   * Method to handle the event when an organisation is deleted.
   *
   * @param {string} id The id of the organisation to delete.
   */
  const handleDeleteOrganisation = (id: string, autoSave: boolean = false): void => {
    const nodeIds: string[] = id.split("|");
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, nodeIds[0]);
    if (currentConfiguration) {
      const updatedOrganisations: MultiTenantConfigOrganisation[] = currentConfiguration.organisations.filter(
        (x) => x.organisationName !== nodeIds[1]
      );
      currentConfiguration.organisations = updatedOrganisations;
      const updatedConfigurations = currentConfigurations.map((x) =>
        (x.id === nodeIds[0] || x.name === nodeIds[0] || x.appName === nodeIds[0]) ? currentConfiguration : x
      );
      updateConfigurationDocuments(updatedConfigurations);
      handleItemSelectedEvent(nodeIds[0]);
      if (autoSave) {
        const cleanedDocument = {
          ...currentConfiguration,
          organisations: currentConfiguration.organisations.map((org) => ({
            ...org,
            namedElasticSettings: org.namedElasticSettings.map((setting) => ({
              ...setting,
              settings: {
                ...setting.settings,
                elasticNodes: setting.settings.elasticNodes.filter((node) => node.trim() !== ""),
              },
            })),
          })),
        };

        if (newConfig) {
          addConfig.mutate(cleanedDocument);
        } else {
          updateConfig.mutate(cleanedDocument);
        }
      } else {
        setDocumentChanged(true);
        updateModifiedItems(nodeIds[0], true);
      }
    }
  };

  /**
   * Method to handle the event when an elastic node is deleted.
   *
   * @param {string} id The id of the elastic node to delete.
   */
  const handleDeleteElasticNode = (id: string, autoSave: boolean = false): void => {
    const nodeIds: string[] = id.split("|");
    const currentConfigurations = [...configDocData];
    const currentConfiguration = findConfigDocument(currentConfigurations, nodeIds[0]);
    if (currentConfiguration) {
      const currentOrganisation = currentConfiguration.organisations.find((x) => x.organisationName === nodeIds[1]);
      if (currentOrganisation) {
        const currentElasticSetting = currentOrganisation.namedElasticSettings.find((x) => x.name === nodeIds[2]);
        if (!currentElasticSetting) return;
        const updatedNodes: string[] = currentElasticSetting.settings.elasticNodes.filter((x) => x !== nodeIds[3]);
        currentElasticSetting.settings.elasticNodes = updatedNodes;
        const updatedConfigurations = currentConfigurations.map((x) =>
          (x.id === nodeIds[0] || x.name === nodeIds[0] || x.appName === nodeIds[0])
            ? {
                ...x,
                organisations: x.organisations.map((y) =>
                  y.organisationName === nodeIds[1] ? currentOrganisation : y
                ),
              }
            : x
        );
        updateConfigurationDocuments(updatedConfigurations);
        handleItemSelectedEvent(`${nodeIds[0]}|${nodeIds[1]}`);
        if (autoSave) {
          const updatedCurrentConfiguration = updatedConfigurations.find((x) => x.appName === nodeIds[0]);
          if (!updatedCurrentConfiguration) return;

          const cleanedDocument = {
            ...updatedCurrentConfiguration,
            organisations: updatedCurrentConfiguration.organisations.map((org) => ({
              ...org,
              namedElasticSettings: org.namedElasticSettings.map((setting) => ({
                ...setting,
                settings: {
                  ...setting.settings,
                  elasticNodes: setting.settings.elasticNodes.filter((node) => node.trim() !== ""),
                },
              })),
            })),
          };

          if (newConfig) {
            addConfig.mutate(cleanedDocument);
          } else {
            updateConfig.mutate(cleanedDocument);
          }
        } else {
          setDocumentChanged(true);
          updateModifiedItems(nodeIds[0], true);
        }
      }
    }
  };

  /**
   * Method to handle the event when the expanded items are changed.
   *
   * @param items The expanded items.
   */
  const handleExpandedItemsChange = (items: string[]): void => {
    setExpanded(items);
  };

  /**
   * The event to handle when the document data is changed.
   *
   * @param data The document data.
   */
  const handleDocumentDataChanged = (data: MultiTenantConfigGetDto): void => {
    setDocumentFormData(data);
    setBreadcrumbs([
      <Typography key="1" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
        <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
        {data?.appName}
      </Typography>,
    ]);
    const updatedConfigDocData = configDocData
      .map((doc) => ((doc.id === data.id || doc.name === data.name) ? data : doc))
      .sort((a, b) =>
        (a.appName ?? "").localeCompare(b.appName ?? "", undefined, { numeric: true, sensitivity: "base" })
      );
    updateConfigurationDocuments(updatedConfigDocData);
    const documentChanged = !ArraysEqual(
      originalConfigDocData.current.sort((a, b) =>
        (a.appName ?? "").localeCompare(b.appName ?? "", undefined, { numeric: true, sensitivity: "base" })
      ),
      updatedConfigDocData
    );
    updateModifiedItems(getConfigDocumentKey(data), documentChanged);
  };

  /**
   * The event to handle when the organisation data is changed.
   *
   * @param data The organisation data.
   */
  const handleOrganisationDataChanged = (data: MultiTenantConfigOrganisation): void => {
    setOrganisationFormData(data);
    if (selectedItemId && selectedItemId.split("|").length > 1) {
      const docId: string = selectedItemId.split("|")[0];
      const originalOrg: string = selectedItemId.split("|")[1];
      const updatedConfigDocData = configDocData
        .map((doc) =>
          (doc.id === docId || doc.name === docId || doc.appName === docId)
            ? {
                ...doc,
                organisations: doc.organisations.map((org) => (org.organisationName === originalOrg ? data : org)),
              }
            : doc
        )
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" }));
      updateConfigurationDocuments(updatedConfigDocData);
      if (originalOrg !== data.organisationName) {
        setSelectedItemId(`${docId}|${data.organisationName}`);
      }
      const currentDoc: MultiTenantConfigGetDto | undefined = findConfigDocument(updatedConfigDocData, docId);
      if (currentDoc) {
        setBreadcrumbs([
          <Link
            underline="hover"
            sx={{ display: "flex", alignItems: "center" }}
            key="1"
            component="button"
            color="inherit"
            onClick={() => handleItemSelectedEvent(getConfigDocumentKey(currentDoc))}
          >
            <AppsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {currentDoc.appName}
          </Link>,
          <Typography key="2" sx={{ color: "text.primary", display: "flex", alignItems: "center" }}>
            <CorporateFareIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {data.organisationName}
          </Typography>,
        ]);
      }
      const organisationChanged = !ArraysEqual(
        originalConfigDocData.current.sort((a, b) =>
          (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" })
        ),
        updatedConfigDocData
      );
      updateModifiedItems(docId, organisationChanged);
    }
  };

  /**
   * The event to handle when the database connection data is changed.
   *
   * @param data The database connection data.
   */
  const handleConnectionDataChanged = (data: DatabaseConnection): void => {
    setConnectionFormData(data);
    if (selectedItemId && selectedItemId.split("|").length > 1) {
      const docId: string = selectedItemId.split("|")[0];
      const originalOrg: string = selectedItemId.split("|")[1];
      const updatedConfigDocData = configDocData
        .map((doc) =>
          (doc.id === docId || doc.name === docId || doc.appName === docId)
            ? {
                ...doc,
                organisations: doc.organisations.map((org) =>
                  org.organisationName === originalOrg
                    ? {
                        ...org,
                        namedConnectionSettings: org.namedConnectionSettings.map((setting) =>
                          setting.name === selectedItemId.split("|")[2] ? { ...setting, settings: data } : setting
                        ),
                      }
                    : org
                ),
              }
            : doc
        )
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" }));
      updateConfigurationDocuments(updatedConfigDocData);
      const dbConnectionChanged = originalConnectionFormData.current !== data;
      updateModifiedItems(docId, dbConnectionChanged);
    }
  };

  /**
   * The event to handle when the elastic index settings data is changed.
   *
   * @param data The index connection data.
   */
  const handleElasticIndexDataChanged = (data: IndexConnection): void => {
    setElasticIndexFormData(data);
    if (selectedItemId && selectedItemId.split("|").length > 1) {
      const parts = selectedItemId.split("|");
      const docId: string = parts[0];
      const originalOrg: string = parts[1];
      const elasticName = parts[2] || elasticSettingSelected || organisationFormData?.namedElasticSettings?.[0]?.name;
      const updatedConfigDocData = configDocData
        .map((doc) =>
          doc.id === docId || doc.name === docId || doc.appName === docId
            ? {
                ...doc,
                organisations: doc.organisations.map((org) => {
                  if (org.organisationName !== originalOrg) return org;
                  const settings = org.namedElasticSettings ?? [];
                  if (!elasticName || settings.length === 0) {
                    return {
                      ...org,
                      namedElasticSettings: [
                        {
                          name: elasticName || "SearchIndexConnection",
                          settings: data,
                        },
                      ],
                    };
                  }
                  return {
                    ...org,
                    namedElasticSettings: settings.map((setting) =>
                      setting.name === elasticName ? { ...setting, settings: data } : setting
                    ),
                  };
                }),
              }
            : doc
        )
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" }));
      updateConfigurationDocuments(updatedConfigDocData);
      const indexConnectionChanged = originalElasticIndexFormData.current !== data;
      updateModifiedItems(docId, indexConnectionChanged);
    }
  };

  /**
   * Method to handle the change event for the elastic node.
   *
   * @param data The URL of the elastic node.
   */
  const handleElasticNodeChangeEvent = (data: string | undefined): void => {
    const newValue = data ?? "";

    if (selectedItemId && selectedItemId.split("|").length > 2) {
      const [appName, orgName, elasticName, originalNodeValue] = selectedItemId.split("|");

      const updatedConfigDocData = configDocData.map((doc) => {
        if (!(doc.id === appName || doc.name === appName || doc.appName === appName)) return doc;

        return {
          ...doc,
          organisations: doc.organisations.map((org) => {
            if (org.organisationName !== orgName) return org;

            return {
              ...org,
              namedElasticSettings: org.namedElasticSettings.map((setting) => {
                if (setting.name !== elasticName) return setting;
                return {
                  ...setting,
                  settings: {
                    ...setting.settings,
                    elasticNodes: setting.settings.elasticNodes.map((node) =>
                      node === originalNodeValue ? newValue : node
                    ),
                  },
                };
              }),
            };
          }),
        };
      });

      updateConfigurationDocuments(updatedConfigDocData);
      setSelectedItemId(`${appName}|${orgName}|${newValue}`);
    }

    setElasticNodeFormData(newValue);
    setEnableSave(true);
  };

  /**
   * Method to handle the event when the user chooses to save the changes made to the current document.
   *
   * @param {string} nodeId The id of the selected node.
   */
  const expandPathToNode = (nodeId: string | undefined): void => {
    if (!nodeId) return;
    const parts = nodeId.split("|");
    const ancestors: string[] = [];
    for (let i = 0; i < parts.length; i++) {
      ancestors.push(parts.slice(0, i + 1).join("|"));
    }
    setExpanded((prev) => Array.from(new Set([...prev, ...ancestors])));
  };

  const handleSaveClicked = () => {
    if (!selectedItemId) return;
    const [documentKey] = selectedItemId.split("|");

    const freshDoc = findConfigDocument(configDocData, documentKey);
    if (!freshDoc) return;

    const validationErrors = validateConfig(configDocData, applicationNamesData, organisationsData, freshDoc);
    if (validationErrors && validationErrors.length > 0) {
      setConfigError(validationErrors);
      const firstErrorNode = validationErrors.find((error) => !!error.treeId)?.treeId;
      if (firstErrorNode) {
        expandPathToNode(firstErrorNode);
        handleItemSelectedEvent(firstErrorNode);
      }
      return;
    }

    setConfigError(undefined);

    const cleanedDocument = {
      ...freshDoc,
      organisations: freshDoc.organisations.map((org) => ({
        ...org,
        namedElasticSettings: org.namedElasticSettings.map((setting) => ({
          ...setting,
          settings: {
            ...setting.settings,
            elasticNodes: setting.settings.elasticNodes.filter((node) => node.trim() !== ""),
          },
        })),
      })),
    };

    if (newConfig) {
      addConfig.mutate(cleanedDocument);
    } else {
      updateConfig.mutate(cleanedDocument);
    }
  };

  /**
   * Method to handle the event when the user chooses to save the changes made to the current document.
   */
  const handleSaveChanges = (): void => {
    setOpenConformation(false);
    handleSaveClicked();
  };

  /**
   * Method to handle the event when the user chooses to dispose of any changes made to the current document.
   */
  const handleAllowDispose = useCallback((): void => {
    setOpenConformation(false);
    let updatedDocuments: MultiTenantConfigGetDto[] = [...configDocData];
    const selectedDocumentId = selectedItemId?.split("|")[0];
    const selectedDocument = findConfigDocument(configDocData, selectedDocumentId);
    const isUnsavedNewDocument =
      !!selectedDocument &&
      !originalConfigDocData.current.some((x) => x.id === selectedDocument.id || (x.id === selectedDocument.id || x.name === selectedDocument.name || x.appName === selectedDocument.appName));

    if (isUnsavedNewDocument) {
      updatedDocuments = configDocData.filter((x) => !(x.id === selectedDocumentId || x.name === selectedDocumentId || x.appName === selectedDocumentId));
      setNewConfig(false);
    } else {
      const currentDocument: MultiTenantConfigGetDto | undefined = findConfigDocument(
        configDocData,
        selectedItemId?.split("|")[0]
      );
      if (currentDocument && originalDocument) {
        const index: number = updatedDocuments.findIndex(
          (x) =>
            x.id === selectedItemId?.split("|")[0] ||
            x.name === selectedItemId?.split("|")[0] ||
            x.appName === selectedItemId?.split("|")[0]
        );
        updatedDocuments[index] = originalDocument;

        if (!!documentFormData) {
          setDocumentFormData(originalDocument);
          originalDocumentFormData.current = originalDocument;
        } else if (!!organisationFormData) {
          setOrganisationFormData(
            originalDocument.organisations.find((x) => x.organisationName === organisationSelected)
          );
          originalOrganisationFormData.current = originalDocument.organisations.find(
            (x) => x.organisationName === organisationSelected
          );
        } else if (!!elasticNodeFormData) {
          setElasticNodeFormData(
            originalDocument.organisations
              .find((x) => x.organisationName === organisationSelected)
              ?.namedElasticSettings.find((x) => x.name === elasticSettingSelected)
              ?.settings.elasticNodes.find((x) => x === elasticNodeSelected)
          );
          originalElasticNodeFormData.current = originalDocument.organisations
            .find((x) => x.organisationName === organisationSelected)
            ?.namedElasticSettings.find((x) => x.name === elasticSettingSelected)
            ?.settings.elasticNodes.find((x) => x === elasticNodeSelected);
        }
      }
    }
    updateConfigurationDocuments(updatedDocuments);
    originalConfigDocData.current = updatedDocuments;
    modifiedItems.current = [];
    setEnableSave(false);
    setConfigError(undefined);
    setDocumentChanged(false);

    const requestedSelection = newSelectedItemId.current;
    const requestedRootId = requestedSelection?.split("|")[0];
    const nextSelection =
      requestedRootId && findConfigDocument(updatedDocuments, requestedRootId)
        ? requestedSelection
        : updatedDocuments[0]
          ? getConfigDocumentKey(updatedDocuments[0])
          : undefined;

    bypassUnsavedPromptRef.current = true;
    handleItemSelectedEvent(nextSelection);
    bypassUnsavedPromptRef.current = false;

    if (nextSelection) {
      document.getElementById(nextSelection)?.scrollIntoView();
    }
  }, [
    configDocData,
    selectedItemId,
    updateConfigurationDocuments,
    handleItemSelectedEvent,
    setDocumentChanged,
    originalDocument,
    documentFormData,
    organisationFormData,
    elasticNodeFormData,
    organisationSelected,
    elasticSettingSelected,
    elasticNodeSelected,
    setNewConfig,
  ]);

  /**
   * Method to handle the event when the user cancels moving away from the dialog.
   */
  const handleCancelMoveAway = (): void => {
    setOpenConformation(false);
    document.getElementById(selectedItemId!)?.scrollIntoView();
    document.getElementById(selectedItemId!)?.focus();
    setClearFilter(true);
  };

  /**
   * Method to handle the add document done event.
   *
   * @param {NewConfigDocumentDto} data The new document application and organisation data.
   */
  const handleActionDialogDone = (data: NewConfigDocumentDto): void => {
    function generateUniqueId() {
      return Math.random().toString(36).substring(2, 9);
    }

    function isIdInConfigDocData(id: string) {
      return !!configDocData.find((x) => x.id === id);
    }

    setNewConfig(true);

    let newId = generateUniqueId();
    while (isIdInConfigDocData(newId)) {
      newId = generateUniqueId();
    }

    const selectedApplicationData = applicationNamesData?.find((app) => app.name === data.appName);

    if (selectedApplicationData) {
      const newDocument: MultiTenantConfigGetDto = {
        appName: data.appName,
        id: newId,
        configType: "MTConfigV2",
        version: 2,
        name: "_New Configuration",
        created: new Date().toISOString(),
        createdBy: displayName ?? "",
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: displayName ?? "",
        organisations: [
          {
            organisationName: data.organisation,
            namedConnectionSettings: selectedApplicationData.dbConnectionNames.map((connectionName) => ({
              name: connectionName,
              settings: {
                server: undefined,
                database: undefined,
                username: undefined,
                password: undefined,
                port: undefined,
              },
            })),
            namedElasticSettings: selectedApplicationData.elasticConnectionNames.map((elasticName) => ({
              name: elasticName,
              settings: {
                alias: undefined,
                username: undefined,
                password: undefined,
                elasticNodes: [],
              },
            })),
          },
        ],
      };

      const updatedConfigurations = [...configDocData];
      updatedConfigurations.push(newDocument);
      updateConfigurationDocuments(updatedConfigurations);

      newSelectedItemId.current = getConfigDocumentKey(newDocument);
      setOriginalDocument(newDocument!);
      setSelectedItemId(getConfigDocumentKey(newDocument));
      setConfigItem(getConfigDocumentKey(newDocument));
      displayDocumentForm(newDocument);
      updateModifiedItems(getConfigDocumentKey(newDocument), true);
      setDocumentChanged(true);
      setEnableSave(true);
      document.getElementById(getConfigDocumentKey(newDocument))?.scrollIntoView();
      document.getElementById(getConfigDocumentKey(newDocument))?.focus();
      setOpenActionDialog(false);
    }
  };

  /**
   * Method to handle the edit named setting close event.
   */
  const handleActionDialogClose = (): void => {
    setOpenActionDialog(false);
  };

  useEffect(() => {
    if (!isLoading && originalConfigDocData.current.length === 0) {
      if (data && data.length > 0) {
        setConfigDocData(data);
        setConfigurationDocuments(data);
        originalConfigDocData.current = data;
        setEnableSave(false);
        setConfigError(undefined);
      } else {
        originalConfigDocData.current = [];
      }
    }
  }, [isLoading, data, setConfigurationDocuments]);

  useEffect(() => {
    if (configDocData.length > 0 && !selectedItemId) {
      const firstId = getConfigDocumentKey(configDocData[0]);
      if (firstId) {
        handleItemSelectedEvent(firstId);
      }
    }
  }, [configDocData, selectedItemId, handleItemSelectedEvent]);

  useEffect(() => {
    setHaveErrors(!!errorText || (configError?.length ?? 0) > 0);

    if (configError && configError.length > 0) {
      const ids: string[] = configError.map((x) => x.treeId).filter((id): id is string => !!id);
      setErrorIds(ids);
    } else {
      setErrorIds([]);
    }
  }, [errorText, configError]);

  useEffect(() => {
    if (disableSave) {
      setEnableSave(false);
      setDisableSave(false);
    }

    if (revertChanges) {
      handleAllowDispose();
      setRevertChanges(false);
    }
  }, [disableSave, setDisableSave, revertChanges, handleAllowDispose, setRevertChanges]);

  useEffect(() => {
    // Only apply store-driven validation errors for the document currently being viewed.
    if (!validationErrors) return;
    const selectedDocumentKey = selectedItemId?.split("|")[0];
    if (!selectedDocumentKey) {
      setConfigError(undefined);
      return;
    }
    const scoped = validationErrors.filter(
      (error) =>
        error.treeId === selectedDocumentKey ||
        error.treeId.startsWith(`${selectedDocumentKey}|`) ||
        error.id === selectedDocumentKey
    );
    setConfigError(scoped.length > 0 ? scoped : undefined);
  }, [validationErrors, selectedItemId]);

  useEffect(() => {
    const selectedDocumentKey = selectedItemId?.split("|")[0];
    const selectedDocument = findConfigDocument(configDocData, selectedDocumentKey);

    if (selectedDocument) {
      const errors = validateConfig(configDocData, applicationNamesData, organisationsData, selectedDocument);
      setConfigError(errors);
    } else {
      setConfigError(undefined);
    }
  }, [configDocData, applicationNamesData, organisationsData, selectedItemId]);

  useEffect(() => {
    if (tokenExpired) {
      logoff();
      localStorage.removeItem(tokenStorageName);
      localStorage.removeItem(loginStorageName);
      localStorage.removeItem(userStorageName);
      setTokenExpired(false);
    }
  }, [tokenExpired, logoff]);

  useEffect(() => {
    if (addConfig.error) {
      setConfigError([
        {
          id: saveItemId.current!,
          treeId: saveItemId.current!,
          organisationName: "",
          field: "name",
          errors: [addConfig.error.message],
        },
      ]);
    }
  }, [addConfig.error]);

  useEffect(() => {
    if (updateConfig.error) {
      setConfigError([
        {
          id: saveItemId.current!,
          treeId: saveItemId.current!,
          organisationName: "",
          field: "name",
          errors: [updateConfig.error.message],
        },
      ]);
    }
  }, [updateConfig.error]);

  return isLoading ? (
    <div>
      <Backdrop open={isLoading}>
        Loading data, please wait...
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  ) : (
    <>
      <Box sx={dataFormStyle("ADSDocumentsPageControl")}>
        <Grid2 container justifyContent="flex-start" spacing={0}>
          <Grid2 size={3}>
            <DocumentTree
              data={configDocData}
              selectedItem={selectedItemId}
              expanded={expanded}
              haveErrors={haveErrors}
              errorIds={errorIds}
              onItemSelected={handleItemSelectedEvent}
              onAddItem={handleAddItem}
              onDeleteItem={handleDeleteItem}
              onExpandedItemsChange={handleExpandedItemsChange}
            />
          </Grid2>
          <Grid2 size={9}>
            <Box sx={toolbarStyle}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ ml: "12px", mt: "8px" }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                  {breadcrumbs}
                </Breadcrumbs>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                  <Button
                    id="config-document-save-button"
                    sx={getSaveButtonStyle(haveErrors)}
                    variant="text"
                    startIcon={!haveErrors ? <SaveIcon /> : <ErrorIcon />}
                    disabled={!enableSave}
                    onClick={handleSaveClicked}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            </Box>
            {(errorText || (configError && configError.length > 0)) && (
              <Box
                sx={{
                  mx: 1.5,
                  mb: 1,
                  p: 1.5,
                  backgroundColor: adsRed10,
                  borderLeft: `4px solid ${adsRed}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PriorityHighIcon sx={{ color: adsRed, mt: 0.25 }} fontSize="small" />
                  <Stack spacing={0.5} sx={{ width: "100%" }}>
                    <Typography variant="subtitle2" sx={{ color: adsRed }}>
                      {errorText
                        ? errorText
                        : `${configError!.length} validation error${configError!.length === 1 ? "" : "s"} in this document`}
                    </Typography>
                    {(configError ?? []).map((error, index) => (
                      <Link
                        key={`config-error-${error.treeId}-${error.field}-${index}`}
                        component="button"
                        underline="hover"
                        align="left"
                        sx={{ color: adsRed, display: "block", textAlign: "left" }}
                        onClick={() => {
                          if (error.field === "namedConnectionSettings" && error.treeId) {
                            handleAddConnectionSetting(error.treeId);
                            return;
                          }
                          if (error.field === "namedElasticSettings" && error.treeId) {
                            handleAddElasticSetting(error.treeId);
                            return;
                          }
                          if (
                            (error.field === "elasticElasticNodes" || error.field === "elasticSettings") &&
                            error.treeId
                          ) {
                            handleAddElasticNode(error.treeId);
                            return;
                          }
                          expandPathToNode(error.treeId);
                          handleItemSelectedEvent(error.treeId);
                        }}
                      >
                        <Typography variant="caption" sx={{ color: adsRed }}>
                          {error.errors.join(", ")}
                        </Typography>
                      </Link>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            )}
            {documentFormData && (
              <DocumentForm data={documentFormData} errors={configError} onDataChange={handleDocumentDataChanged} />
            )}
            {organisationFormData && (
              <DocumentOrganisationForm
                appName={newSelectedItemId.current!.split("|")[0]}
                data={organisationFormData}
                errors={configError}
                onDataChange={handleOrganisationDataChanged}
              />
            )}
            {connectionFormData && (
              <DbConnectionForm
                parentTreeId={newSelectedItemId.current!}
                data={connectionFormData}
                errors={configError}
                onDataChange={handleConnectionDataChanged}
              />
            )}
            {elasticIndexFormData && (
              <IndexConnectionForm
                parentTreeId={
                  elasticSettingSelected
                    ? `${selectedItemId?.split("|").slice(0, 2).join("|")}|${elasticSettingSelected}`
                    : selectedItemId ?? ""
                }
                data={elasticIndexFormData}
                errors={configError}
                onDataChange={handleElasticIndexDataChanged}
                onAddElasticNode={() => {
                  const orgPath = selectedItemId?.split("|").slice(0, 2).join("|");
                  const elasticPath = elasticSettingSelected
                    ? `${orgPath}|${elasticSettingSelected}`
                    : orgPath;
                  if (elasticPath) {
                    // Ensure elastic setting exists on the org, then add a node.
                    if (!elasticSettingSelected && orgPath) {
                      handleAddElasticSetting(orgPath);
                    } else {
                      handleAddElasticNode(elasticPath);
                    }
                  }
                }}
              />
            )}
            {elasticNodeFormData !== undefined && (
              <ElasticNodeForm
                parentTreeId={newSelectedItemId.current!}
                data={elasticNodeFormData}
                errors={configError}
                onDataChange={handleElasticNodeChangeEvent}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>
      <ConfirmEditLossDialog
        isOpen={openConformation}
        title="Unsaved changes"
        message="You have made changes to this record, do you want to keep them or discard them?"
        saveText="Keep"
        handleSaveClick={handleSaveChanges}
        handleDisposeClick={handleAllowDispose}
        handleReturnClick={handleCancelMoveAway}
      />
      {openActionDialog && actionType.current !== undefined && (
        <ActionDialog
          open={openActionDialog}
          mode={actionMode.current!}
          variant={actionType.current}
          data={actionData.current}
          onDone={handleActionDialogDone}
          onClose={handleActionDialogClose}
        />
      )}
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        variant={deleteType.current ?? DeleteVariant.elasticNode}
        recordName={deleteRecordName.current}
        onClose={handleCloseDeleteConfirmation}
      />
    </>
  );
};

export default DocumentsPage;
